import keys from 'client/components/txtkeys/subscriptionKeys'
import { QuotaTypes, SubscriptionPlanInterface } from 'client/Interfaces/Subscription'
import {
  ISubscriptionFeatureItem,
  SubscriptionFeatureCheckboxType,
  SubscriptionFeatureType,
} from 'client/Interfaces/Subscription/ISubscriptionFeatureList'
import {
  subscriptionAvailablePlansCollectionSelector,
  subscriptionCompanySubscriptionSelector,
  subscriptionIsAvailablePlansLoadedSelector,
} from 'client/selectors/subscription'
// typescript
import { setBaseClass } from 'client/utils'
import * as React from 'react'
import { connect } from 'react-redux'

import { IAvailablePlanNames, TPremiumPlansFeatures } from './IPremiumInterface'
import { plansSelector } from './plansSelector'
import './PremiumPlanCollection.scss'
import { PremiumPlanGrouped, PremiumPlanOption } from './PremiumPlanOption'

export const freePlan: SubscriptionPlanInterface = {
  id: 'plan.free',
  name: IAvailablePlanNames['plan.free'],
  price: 0,
  currency: '€',
  quotas: [
    { name: QuotaTypes.FREE_MOBILE, quota: 0 },
    { name: QuotaTypes.INVOICE, quota: 0 },
    { name: QuotaTypes.CUSTOMERS, quota: 5 },
  ],
  billingCycle: 'month',
}

const cls = setBaseClass('PremiumPlanCollection')

const createFeature = (
  id: QuotaTypes,
  type: SubscriptionFeatureType,
  translationKeyMonth: string,
  translationKeyYear?: string | undefined,
) => ({
  id,
  type,
  translationKeyMonth,
  translationKeyYear: translationKeyYear || translationKeyMonth,
  checkboxStyle: SubscriptionFeatureCheckboxType.GREEN,
})

const features: { [key: string]: Omit<ISubscriptionFeatureItem, 'amount'> } = {
  accountantAccess: createFeature(
    QuotaTypes.ACCOUNTANT_ACCESS,
    SubscriptionFeatureType.ACCOUNTING,
    keys.accountantAccess,
  ),
  AIExpensesCategorization: createFeature(
    QuotaTypes.AI_EXPENSES_CATEGORIZATION,
    SubscriptionFeatureType.ACCOUNTING,
    keys.AIExpensesCategorization,
  ),
  attachment: createFeature(
    QuotaTypes.EMAIL_ATTACHMENT,
    SubscriptionFeatureType.INVOICING,
    keys.emailAttachmentFeature,
  ),
  autoBankReconciliation: createFeature(
    QuotaTypes.AUTO_BANK_RECONCILIATION,
    SubscriptionFeatureType.ACCOUNTING,
    keys.autoBankReconciliation,
  ),
  banking: createFeature(QuotaTypes.BANKING, SubscriptionFeatureType.ACCOUNTING, keys.banking),
  bankReconciliation: createFeature(
    QuotaTypes.BANK_RECONCILIATION,
    SubscriptionFeatureType.ACCOUNTING,
    keys.bankReconciliation,
  ),
  bookkeepingSupport: createFeature(
    QuotaTypes.BOOKKEEPING_SUPPORT,
    SubscriptionFeatureType.ACCOUNTING,
    keys.bookkeepingSupport,
  ),
  createEstimates: createFeature(
    QuotaTypes.FREE_ESTIMATES,
    SubscriptionFeatureType.INVOICING,
    keys.freeCreateEstimatesFeature,
  ),
  createInvoice: createFeature(
    QuotaTypes.FREE_INVOICING,
    SubscriptionFeatureType.INVOICING,
    keys.freeCreateInvoiceFeature,
  ),
  customers: createFeature(QuotaTypes.CUSTOMERS, SubscriptionFeatureType.INVOICING, keys.customersFeature),
  documentCustomisation: createFeature(
    QuotaTypes.CUSTOM_INVOICE_STYLING,
    SubscriptionFeatureType.INVOICING,
    keys.documentCustomisationFeature,
  ),
  downPayment: createFeature(QuotaTypes.DOWNPAYMENT, SubscriptionFeatureType.INVOICING, keys.downPayment),
  eInvoice: createFeature(QuotaTypes.E_INVOICE, SubscriptionFeatureType.INVOICING, keys.eInvoice),
  email: createFeature(QuotaTypes.FREE_EMAIL, SubscriptionFeatureType.INVOICING, keys.freeSendEmailAndPdf),
  estimate: createFeature(QuotaTypes.ESTIMATES, SubscriptionFeatureType.INVOICING, keys.estimateConvertFeature),
  expenses: createFeature(QuotaTypes.EXPENSES, SubscriptionFeatureType.ACCOUNTING, keys.expenses),
  invoice: createFeature(
    QuotaTypes.INVOICE,
    SubscriptionFeatureType.INVOICING,
    keys.premiumInvoicesPerMonth,
    keys.premiumInvoicesPerYear,
  ),
  invoicesEstimates: createFeature(
    QuotaTypes.INVOICES_ESTIMATES,
    SubscriptionFeatureType.INVOICING,
    keys.invoicesEstimates,
  ),
  limitedInvoicesEstimates: createFeature(
    QuotaTypes.LIMITED_INVOICES_ESTIMATES,
    SubscriptionFeatureType.INVOICING,
    keys.limitedInvoicesEstimates,
  ),
  mobile: createFeature(QuotaTypes.FREE_MOBILE, SubscriptionFeatureType.ADMIN, keys.freeMobileApp),
  onlineBankPayments: createFeature(
    QuotaTypes.ONLINE_BANK_PAYMENTS,
    SubscriptionFeatureType.ADMIN,
    keys.onlineBankPayments,
  ),
  onlineCardPayments: createFeature(
    QuotaTypes.ONLINE_CARD_PAYMENTS,
    SubscriptionFeatureType.ADMIN,
    keys.onlineCardPayments,
  ),
  postalInvoice: createFeature(QuotaTypes.INVOICE, SubscriptionFeatureType.INVOICING, keys.postalInvoice),
  products: createFeature(QuotaTypes.FREE_PRODUCTS, SubscriptionFeatureType.INVOICING, keys.freeProductsFeature),
  receiptScanning: createFeature(QuotaTypes.RECEIPT_SCANNING, SubscriptionFeatureType.ACCOUNTING, keys.receiptScanning),
  recurringProfile: createFeature(
    QuotaTypes.RECURRING_PROFILE,
    SubscriptionFeatureType.INVOICING,
    keys.recurringProfileFeature,
  ),
  reminders: createFeature(QuotaTypes.REMINDERS, SubscriptionFeatureType.INVOICING, keys.reminderFeature),
  reports: createFeature(QuotaTypes.FREE_REPORTING, SubscriptionFeatureType.INVOICING, keys.freeReportsFeature),
  schedule: createFeature(QuotaTypes.PAYMENT_SCHEDULE, SubscriptionFeatureType.INVOICING, keys.paymentScheduleFeature),
  timesheet: createFeature(QuotaTypes.TIMESHEET, SubscriptionFeatureType.INVOICING, keys.trackerMarketingTitle),
  tradenames: createFeature(QuotaTypes.TRADENAMES, SubscriptionFeatureType.ADMIN, keys.tradenamesFeature),
  VATReturns: createFeature(QuotaTypes.VAT_RETURNS, SubscriptionFeatureType.ACCOUNTING, keys.VATReturns),
}

const commonFeatures = {
  highlights: [features.customers, features.invoicesEstimates, features.invoice],
  items: [
    features.mobile,
    features.estimate,
    features.reminders,
    features.attachment,
    features.downPayment,
    features.schedule,
    features.tradenames,
    features.timesheet,
    features.recurringProfile,
    features.banking,
    features.onlineBankPayments,
    features.documentCustomisation,
  ],
}

export const premiumPlansFeatures: TPremiumPlansFeatures = {
  'plan.none': {},
  'plan.free': {
    highlights: [features.customers, features.limitedInvoicesEstimates, features.invoice],
    items: [features.mobile],
  },
  'plan.ageras_free': {
    highlights: [],
    items: [
      features.customers,
      features.limitedInvoicesEstimates,
      features.createEstimates,
      features.timesheet,
      features.expenses,
      features.bankReconciliation,
      features.AIExpensesCategorization,
      features.onlineBankPayments,
      features.onlineCardPayments,
      features.mobile,
    ],
  },
  'plan.starter': {
    highlights: commonFeatures.highlights,
    items: [
      features.mobile,
      features.estimate,
      features.reminders,
      features.attachment,
      features.onlineBankPayments,
      features.documentCustomisation,
    ],
  },
  'plan.ageras_start': {
    highlights: [],
    items: [
      features.customers,
      features.invoicesEstimates,
      features.eInvoice,
      features.createEstimates,
      features.timesheet,
      features.reminders,
      features.estimate,
      features.expenses,
      features.bankReconciliation,
      features.AIExpensesCategorization,
      features.onlineBankPayments,
      features.onlineCardPayments,
      features.mobile,
      features.documentCustomisation,
    ],
  },
  'plan.pro': commonFeatures,
  'plan.ageras_basic': {
    highlights: [],
    items: [
      features.customers,
      features.invoicesEstimates,
      features.eInvoice,
      features.invoice,
      features.createEstimates,
      features.timesheet,
      features.reminders,
      features.estimate,
      features.downPayment,
      features.schedule,
      features.attachment,
      features.expenses,
      features.bankReconciliation,
      features.AIExpensesCategorization,
      features.bookkeepingSupport,
      features.accountantAccess,
      features.VATReturns,
      features.onlineBankPayments,
      features.onlineCardPayments,
      features.mobile,
      features.documentCustomisation,
    ],
  },
  'plan.growth': commonFeatures,
  'plan.ageras_plus': {
    highlights: [],
    items: [
      features.customers,
      features.invoicesEstimates,
      features.eInvoice,
      features.invoice,
      features.createEstimates,
      features.timesheet,
      features.reminders,
      features.estimate,
      features.downPayment,
      features.schedule,
      features.attachment,
      features.recurringProfile,
      features.expenses,
      features.bankReconciliation,
      features.AIExpensesCategorization,
      features.bookkeepingSupport,
      features.accountantAccess,
      features.VATReturns,
      features.receiptScanning,
      features.autoBankReconciliation,
      features.onlineBankPayments,
      features.onlineCardPayments,
      features.tradenames,
      features.mobile,
      features.documentCustomisation,
    ],
  },
  'plan.merchant': commonFeatures,
  'plan.contact_us': commonFeatures,
}

const PremiumPlansComponent = (props) => {
  const {
    isAvailablePlansLoaded,
    subscription,
    hideDowngradePlans,
    onCancelDowngrade,
    planFilters,
    onPlanSelection,
    dateFormat,
    locale,
    plans,
    allPlans,
    settings,
    company,
  } = props

  if (!isAvailablePlansLoaded || !subscription) {
    return null
  }

  return (
    <div className={cls()}>
      <div className="groupedPlans">
        {plans.individualPlans.map((plan, i) => {
          return (
            <PremiumPlanOption
              key={plan.name}
              plan={plan}
              subscription={subscription}
              premiumPlansFeatures={premiumPlansFeatures}
              hideDowngradePlans={hideDowngradePlans}
              planFilters={planFilters}
              dateFormat={dateFormat}
              locale={locale}
              onCancelDowngrade={onCancelDowngrade}
              onPlanSelection={onPlanSelection}
              allPlans={allPlans}
              settings={settings}
              company={company}
            />
          )
        })}
      </div>
      <div className={`groupedPlans${!planFilters?.hideFreePlan ? ' --free' : ''}`}>
        <PremiumPlanGrouped
          isCombinedPlan={false}
          key="grouped-plans1"
          groupedPlans={plans.groupedPlans}
          subscription={subscription}
          premiumPlansFeatures={premiumPlansFeatures}
          hideDowngradePlans={hideDowngradePlans}
          planFilters={planFilters}
          allPlans={allPlans}
          dateFormat={dateFormat}
          locale={locale}
          onCancelDowngrade={onCancelDowngrade}
          onPlanSelection={onPlanSelection}
          settings={settings}
        />
        <PremiumPlanGrouped
          isCombinedPlan={true}
          key="grouped-plans2"
          groupedPlans={plans.groupedPlans.slice(1)}
          subscription={subscription}
          premiumPlansFeatures={premiumPlansFeatures}
          hideDowngradePlans={hideDowngradePlans}
          planFilters={planFilters}
          allPlans={allPlans}
          dateFormat={dateFormat}
          locale={locale}
          onCancelDowngrade={onCancelDowngrade}
          onPlanSelection={onPlanSelection}
          settings={settings}
        />
      </div>
    </div>
  )
}

// subscription
const mapStateToProps = (state, ownProps) => {
  const { planFilters } = ownProps

  return {
    isAvailablePlansLoaded: subscriptionIsAvailablePlansLoadedSelector(state),
    plans: plansSelector(state, { planFilters }),
    // we need to include this
    allPlans: subscriptionAvailablePlansCollectionSelector(state),
    subscription: subscriptionCompanySubscriptionSelector(state),
    settings: state.settings,
    company: state.company,
    experiments: state.experiments,
  }
}

const PremiumPlanCollection = connect(mapStateToProps)(PremiumPlansComponent)

export { PremiumPlanCollection }
